<template>
  <div class="pct-tier-multi-ladder-view">
    <!-- 控制栏 -->
    <div class="row align-items-center mb-2 px-2">
      <div class="d-flex flex-wrap align-items-center w-100 ladder-toolbar">
        <!-- 截止日期 -->
        <div class="control-item">
          <b-form-datepicker 
            id="pct-ladder-datepicker" 
            size="sm" 
            :date-disabled-fn="dateDisabled" 
            v-model="end_date" 
            today-button 
            close-button 
            locale="zh"
          ></b-form-datepicker>
        </div>

        <!-- 统计窗口天数 (10/20/30天) -->
        <div class="control-item">
          <b-form-select v-model="window_days" :options="windowOptions" size="sm"></b-form-select>
        </div>

        <!-- 展示天数 -->
        <div class="control-item">
          <b-form-select v-model="display_days" :options="displayDaysOptions" size="sm"></b-form-select>
        </div>

        <!-- 梯队类型 (上涨/下跌/全部) -->
        <div class="control-item">
          <b-form-select v-model="tier_type" :options="tierTypeOptions" size="sm"></b-form-select>
        </div>

        <!-- 行情开关 -->
        <div class="control-item">
          <b-form-checkbox id="cb_pct_market" v-model.number="status_market" :value="1" :unchecked-value="0" size="sm">行情</b-form-checkbox>
        </div>

        <!-- 题材显隐 -->
        <div class="control-item">
          <b-form-checkbox id="cb_pct_concept" v-model.number="show_concept" :value="1" :unchecked-value="0" size="sm">题材</b-form-checkbox>
        </div>

        <!-- 个数显隐 -->
        <div class="control-item">
          <b-form-checkbox id="cb_pct_count" v-model.number="show_count" :value="1" :unchecked-value="0" size="sm">个数</b-form-checkbox>
        </div>

        <!-- 价格显隐 -->
        <div class="control-item">
          <b-form-checkbox id="cb_pct_price" v-model.number="show_price" :value="1" :unchecked-value="0" size="sm">价格</b-form-checkbox>
        </div>

        <!-- 同股追踪高亮 -->
        <div class="control-item">
          <b-form-checkbox id="cb_pct_highlight" v-model.number="highlight_same_stock" :value="1" :unchecked-value="0" size="sm">同股追踪</b-form-checkbox>
        </div>

        <!-- 刷新按钮 -->
        <div class="control-item ml-auto">
          <b-button size="sm" variant="primary" :disabled="loading" @click="fetchLadderData">
            <b-spinner small v-if="loading" class="mr-1"></b-spinner>
            <span>刷新</span>
          </b-button>
        </div>
      </div>
    </div>

    <!-- 加载中遮罩 -->
    <div v-if="loading && dates.length === 0" class="text-center py-5">
      <b-spinner variant="primary" label="加载中..."></b-spinner>
      <div class="text-muted mt-2 small">正在组装多日涨跌幅天梯数据...</div>
    </div>

    <!-- 空数据提示 -->
    <div v-else-if="dates.length === 0" class="text-center py-5 empty-card">
      <div class="empty-icon">📊</div>
      <div class="text-muted mt-2">暂无该区间的涨幅梯队数据</div>
      <div class="text-muted small mt-1">请尝试切换更早日期或执行数据同步</div>
    </div>

    <!-- 多日天梯矩阵容器：支持横向与纵向平滑滚动 -->
    <div v-else class="ladder-scroll-wrapper" ref="scrollContainer">
      <div class="ladder-grid" :style="{ minWidth: (dates.length * 160 + 105) + 'px' }">
        <!-- 头部日期行 -->
        <div class="ladder-row ladder-header-row no-gutters flex-nowrap">
          <!-- 左上角固定角标 -->
          <div class="ladder-header-col corner-col border d-flex flex-column align-items-center justify-content-center text-center">
            <span class="corner-title">梯队 / 日期</span>
            <span class="corner-sub">{{ dates.length }}日 · {{ window_days }}天区间</span>
          </div>

          <!-- 各交易日列头 (最新列在最前面/最左侧) -->
          <div 
            class="ladder-day-col border text-center d-flex flex-column justify-content-center py-1 px-1"
            v-for="(dateStr, idx) in dates" 
            :key="'header-' + dateStr"
            :class="{ 'latest-col-header': idx === 0 }"
          >
            <div class="date-header-top d-flex align-items-center justify-content-center">
              <span class="date-short">{{ formatDateShort(dateStr) }}</span>
              <span class="weekday-badge ml-1">{{ getWeekday(dateStr) }}</span>
              <span v-if="idx === 0" class="latest-tag ml-1">最新</span>
            </div>
            <div class="date-header-bottom text-muted small mt-1" v-if="show_count">
              <span>{{ getDateStockCount(dateStr) }}只</span>
              <span class="dot-sep" v-if="getDateMaxPct(dateStr)">·</span>
              <span class="max-pct-text" v-if="getDateMaxPct(dateStr)">最高+{{ getDateMaxPct(dateStr) }}%</span>
            </div>
          </div>
        </div>

        <!-- 涨跌幅梯队行 (从最高涨幅区间降序排列) -->
        <div 
          class="ladder-row no-gutters flex-nowrap" 
          v-for="tier in tier_names" 
          :key="'tier-' + tier"
        >
          <!-- 左侧梯队层级标签 -->
          <div class="ladder-header-col tier-col border d-flex flex-column align-items-center justify-content-center py-1">
            <div class="tier-title-wrap">
              <span class="tier-name-badge" :style="getTierBadgeStyle(tier)">{{ tier }}</span>
              <span v-if="show_count && getTierLatestCount(tier) > 0" class="latest-tier-count ml-1">
                {{ getTierLatestCount(tier) }}只
              </span>
            </div>
            <span class="tier-total-hint small" v-if="show_count">
              区间{{ getTierTotalCount(tier) }}只次
            </span>
          </div>

          <!-- 对应各交易日的格子 -->
          <div 
            class="ladder-day-col border ladder-content-cell p-1"
            v-for="(dateStr, dIdx) in dates" 
            :key="'cell-' + tier + '-' + dateStr"
            :class="{ 'latest-col-cell': dIdx === 0, 'empty-cell': !hasStocks(tier, dateStr) }"
          >
            <!-- 股票列表 -->
            <div 
              class="stock-item-card mb-1" 
              v-for="stock in getStocks(tier, dateStr)" 
              :key="stock.stock_code + '_' + dateStr + '_' + tier"
              :class="{ 
                'same-stock-highlight': highlight_same_stock && hoveredStockCode === stock.stock_code,
                'is-down-card': stock.pct < 0
              }"
              @mouseenter="onStockHover(stock.stock_code)"
              @mouseleave="onStockLeave"
              @click="onStockClick(stock)"
              v-b-toggle.sidebar-right-stock
              v-b-popover.hover.top="getStockPopover(stock, tier)"
            >
              <!-- 首行：市场标 + 股票名称 + 区间涨跌幅 + 实时行情 -->
              <div class="stock-card-top d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center text-truncate">
                  <!-- 市场类型小标 -->
                  <span v-if="stock.stock_code.startsWith('30')" class="circle-badge circle-c mr-1">ⓒ</span>
                  <span v-else-if="stock.stock_code.startsWith('68')" class="circle-badge circle-k mr-1">Ⓚ</span>
                  <span v-else-if="stock.stock_code.startsWith('8') || stock.stock_code.startsWith('9')" class="circle-badge circle-b mr-1">Ⓑ</span>
                  <span class="stock-name font-weight-bold">{{ stock.stock_name }}</span>
                </div>
                <!-- 区间累计涨幅 -->
                <div class="stock-pct-rate ml-1" :class="stock.pct >= 0 ? 'pct-up' : 'pct-down'">
                  {{ stock.pct > 0 ? '+' : '' }}{{ stock.pct }}%
                </div>
              </div>

              <!-- 次行：实时行情（如果开启） -->
              <div v-if="status_market && realQuotes[stock.stock_code]" class="stock-real-row d-flex align-items-center justify-content-between mt-1 small">
                <span class="text-muted">实时</span>
                <span :style="getQuoteStyle(realQuotes[stock.stock_code])">
                  {{ realQuotes[stock.stock_code].px_change_rate }}%
                </span>
              </div>

              <!-- 题材概念标签 -->
              <div v-if="show_concept && getConcepts(stock).length > 0" class="stock-card-plates mt-1">
                <span 
                  class="plate-badge" 
                  v-for="(c, cIdx) in getConcepts(stock).slice(0, 2)" 
                  :key="cIdx"
                >{{ c }}</span>
                <span v-if="getConcepts(stock).length > 2" class="plate-more">+{{ getConcepts(stock).length - 2 }}</span>
              </div>

              <!-- 收盘价展示 -->
              <div v-if="show_price && stock.close_price" class="stock-card-price mt-1 text-muted small">
                ¥{{ Number(stock.close_price).toFixed(2) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'

export default {
  name: 'PctTierMultiDayLadder',
  data() {
    return {
      reqId: 0,
      loading: false,
      end_date: '',
      window_days: 10, // 统计窗口天数 (10/20/30)
      display_days: 12, // 展示交易日天数
      tier_type: 'up', // 'up' | 'down' | 'all'
      windowOptions: [
        { value: 10, text: '10日区间 (默认)' },
        { value: 20, text: '20日区间' },
        { value: 30, text: '30日区间' }
      ],
      displayDaysOptions: [
        { value: 5, text: '最近 5 天' },
        { value: 8, text: '最近 8 天' },
        { value: 10, text: '最近 10 天' },
        { value: 12, text: '最近 12 天 (默认)' },
        { value: 15, text: '最近 15 天' },
        { value: 20, text: '最近 20 天' },
        { value: 30, text: '最近 30 天' }
      ],
      tierTypeOptions: [
        { value: 'up', text: '上涨梯队 (默认)' },
        { value: 'down', text: '下跌梯队' },
        { value: 'all', text: '全部 (涨+跌)' }
      ],
      status_market: 0,
      show_concept: 1,
      show_count: 1,
      show_price: 0,
      highlight_same_stock: 1,
      hoveredStockCode: '',
      // 后端返回的数据
      dates: [],
      tier_names: [],
      date_info: {},
      ladder: {},
      all_stocks: [],
      realQuotes: {},
      all_trading_days: []
    }
  },
  mounted() {
    // 从 localStorage 恢复用户习惯
    const savedWindow = localStorage.getItem('pct_tier_ladder_window_days')
    const savedDisplay = localStorage.getItem('pct_tier_ladder_display_days')
    const savedType = localStorage.getItem('pct_tier_ladder_tier_type')
    const savedMarket = localStorage.getItem('pct_tier_ladder_status_market')
    const savedConcept = localStorage.getItem('pct_tier_ladder_show_concept')
    const savedCount = localStorage.getItem('pct_tier_ladder_show_count')
    const savedPrice = localStorage.getItem('pct_tier_ladder_show_price')
    const savedHighlight = localStorage.getItem('pct_tier_ladder_highlight_same_stock')

    if (savedWindow !== null) this.window_days = parseInt(savedWindow)
    if (savedDisplay !== null) this.display_days = parseInt(savedDisplay)
    if (savedType !== null) this.tier_type = savedType
    if (savedMarket !== null) this.status_market = parseInt(savedMarket)
    if (savedConcept !== null) this.show_concept = parseInt(savedConcept)
    if (savedCount !== null) this.show_count = parseInt(savedCount)
    if (savedPrice !== null) this.show_price = parseInt(savedPrice)
    if (savedHighlight !== null) this.highlight_same_stock = parseInt(savedHighlight)

    // 查询最新交易日
    request_zzquant({ url: '/v3/market/trade/days' }).then(res => {
      const days = res && res.data && res.data.data ? res.data.data : []
      if (days && days.length > 0) {
        this.all_trading_days = days
        this.end_date = days[days.length - 1]
      } else {
        const now = new Date()
        const y = now.getFullYear()
        const m = String(now.getMonth() + 1).padStart(2, '0')
        const d = String(now.getDate()).padStart(2, '0')
        this.end_date = `${y}-${m}-${d}`
      }
      this.fetchLadderData()
    }).catch(() => {
      const now = new Date()
      const y = now.getFullYear()
      const m = String(now.getMonth() + 1).padStart(2, '0')
      const d = String(now.getDate()).padStart(2, '0')
      this.end_date = `${y}-${m}-${d}`
      this.fetchLadderData()
    })
  },
  watch: {
    end_date(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.fetchLadderData()
      }
    },
    window_days(newVal) {
      localStorage.setItem('pct_tier_ladder_window_days', newVal)
      this.fetchLadderData()
    },
    display_days(newVal) {
      localStorage.setItem('pct_tier_ladder_display_days', newVal)
      this.fetchLadderData()
    },
    tier_type(newVal) {
      localStorage.setItem('pct_tier_ladder_tier_type', newVal)
      this.fetchLadderData()
    },
    status_market(newVal) {
      localStorage.setItem('pct_tier_ladder_status_market', newVal)
      if (newVal === 1 && this.all_stocks.length > 0) {
        this.fetchRealQuotes()
      }
    },
    show_concept(newVal) {
      localStorage.setItem('pct_tier_ladder_show_concept', newVal)
    },
    show_count(newVal) {
      localStorage.setItem('pct_tier_ladder_show_count', newVal)
    },
    show_price(newVal) {
      localStorage.setItem('pct_tier_ladder_show_price', newVal)
    },
    highlight_same_stock(newVal) {
      localStorage.setItem('pct_tier_ladder_highlight_same_stock', newVal)
    }
  },
  methods: {
    dateDisabled(ymd, date) {
      const weekday = date.getDay()
      if (weekday === 0 || weekday === 6) return true
      if (date.getTime() >= new Date().getTime()) return true
      return false
    },
    fetchLadderData() {
      if (!this.end_date) return
      const curReqId = ++this.reqId
      this.loading = true

      const url = `/v3/market/pct-tier/ladder?end_date=${this.end_date}&window_days=${this.window_days}&display_days=${this.display_days}&tier_type=${this.tier_type}`
      request_zzquant({ url }).then(res => {
        if (curReqId !== this.reqId) return
        const data = res && res.data && res.data.data ? res.data.data : null
        if (data) {
          this.dates = data.dates || []
          this.tier_names = (data.tier_names || []).filter(t => t !== '20%~40%' && t !== '20~40' && t !== '-20%~-40%' && t !== '-20~-40')
          this.date_info = data.date_info || {}
          this.ladder = data.ladder || {}
          this.all_stocks = data.stocks || []

          // 如果开启了行情展示，拉取实时行情
          if (this.status_market === 1 && this.all_stocks.length > 0) {
            this.fetchRealQuotes()
          }
        }
        this.loading = false
      }).catch(err => {
        console.error('fetchLadderData error:', err)
        if (curReqId !== this.reqId) return
        this.loading = false
      })
    },
    fetchRealQuotes() {
      if (!this.all_stocks || this.all_stocks.length === 0) return
      const symbols = this.all_stocks.slice(0, 200).join(',')
      request_zzquant({ url: `/v3/open/market/real?symbols=${symbols}` }).then(res => {
        const d = res && res.data && res.data.data
        if (d && d.fields && d.values) {
          const quotes = {}
          const fields = d.fields
          const values = d.values
          for (let i = 0; i < fields.length; i++) {
            quotes[fields[i]] = values[i]
          }
          this.realQuotes = quotes
        }
      }).catch(err => {
        console.warn('fetchRealQuotes error:', err)
      })
    },
    getStocks(tier, dateStr) {
      if (this.ladder[tier] && this.ladder[tier][dateStr]) {
        return this.ladder[tier][dateStr]
      }
      return []
    },
    hasStocks(tier, dateStr) {
      return this.getStocks(tier, dateStr).length > 0
    },
    formatDateShort(dateStr) {
      if (!dateStr || dateStr.length < 10) return dateStr
      return dateStr.substring(5) // '10-21'
    },
    getWeekday(dateStr) {
      if (this.date_info[dateStr] && this.date_info[dateStr].weekday) {
        return this.date_info[dateStr].weekday
      }
      return ''
    },
    getDateStockCount(dateStr) {
      if (this.date_info[dateStr] && this.date_info[dateStr].stock_count !== undefined) {
        return this.date_info[dateStr].stock_count
      }
      return 0
    },
    getDateMaxPct(dateStr) {
      if (this.date_info[dateStr] && this.date_info[dateStr].max_pct !== undefined && this.date_info[dateStr].max_pct !== null) {
        return this.date_info[dateStr].max_pct
      }
      return null
    },
    getTierLatestCount(tier) {
      if (!this.dates || this.dates.length === 0) return 0
      const latestDate = this.dates[0]
      return this.getStocks(tier, latestDate).length
    },
    getTierTotalCount(tier) {
      let total = 0
      for (let i = 0; i < this.dates.length; i++) {
        total += this.getStocks(tier, this.dates[i]).length
      }
      return total
    },
    getTierBadgeStyle(tier) {
      if (tier.startsWith('≥100')) {
        return { color: '#dc2626', fontWeight: '800' }
      } else if (tier.startsWith('80%')) {
        return { color: '#ef4444', fontWeight: '700' }
      } else if (tier.startsWith('60%')) {
        return { color: '#f97316', fontWeight: '600' }
      } else if (tier.startsWith('40%')) {
        return { color: '#eab308', fontWeight: '600' }
      } else if (tier.startsWith('20%')) {
        return { color: '#38bdf8', fontWeight: '500' }
      } else if (tier.startsWith('-20%')) {
        return { color: '#86efac', fontWeight: '500' }
      } else if (tier.startsWith('-40%')) {
        return { color: '#4ade80', fontWeight: '600' }
      } else if (tier.startsWith('-60%')) {
        return { color: '#22c55e', fontWeight: '700' }
      } else if (tier.startsWith('≤-80')) {
        return { color: '#16a34a', fontWeight: '800' }
      }
      return { color: 'var(--text-main)', fontWeight: '500' }
    },
    getConcepts(stock) {
      if (!stock || !stock.concept) return []
      return stock.concept.split(/[,，/ ]+/).filter(c => c && c.trim().length > 0)
    },
    getQuoteStyle(quote) {
      if (!quote || quote.px_change_rate === undefined) return {}
      const rate = Number(quote.px_change_rate)
      if (rate > 0) return { color: '#ef4444', fontWeight: '600' }
      if (rate < 0) return { color: '#22c55e', fontWeight: '600' }
      return { color: 'var(--text-muted)' }
    },
    onStockHover(code) {
      if (this.highlight_same_stock) {
        this.hoveredStockCode = code
      }
    },
    onStockLeave() {
      this.hoveredStockCode = ''
    },
    onStockClick(stock) {
      this.$emit('show_sidebar', {
        stock_code: stock.stock_code,
        stock_name: stock.stock_name,
        date1: stock.date1
      })
    },
    getStockPopover(stock, tier) {
      const parts = [
        `【${stock.stock_name} (${stock.stock_code})】`,
        `日期: ${stock.date1}  区间: ${this.window_days}天累计`,
        `涨跌幅: ${stock.pct > 0 ? '+' : ''}${stock.pct}% [${tier}]`
      ]
      if (stock.market_type) {
        parts.push(`板块: ${stock.market_type}`)
      }
      if (stock.close_price) {
        parts.push(`区间收盘价: ¥${Number(stock.close_price).toFixed(2)}`)
      }
      if (stock.concept) {
        parts.push(`题材概念: ${stock.concept}`)
      }
      return parts.join('\n')
    }
  }
}
</script>

<style scoped>
.pct-tier-multi-ladder-view {
  overflow-x: hidden;
  padding-bottom: 24px;
}

.ladder-toolbar {
  gap: 8px;
}

.control-item {
  display: flex;
  align-items: center;
}

.empty-card {
  background: var(--bg-card, #1e2638);
  border: 1px solid var(--border-color, #2d3748);
  border-radius: 8px;
  padding: 40px 20px;
}

.empty-icon {
  font-size: 36px;
}

/* 横向与纵向平滑滚动容器 */
.ladder-scroll-wrapper {
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 8px;
  background: var(--bg-card, #1e2638);
  border: 1px solid var(--border-color, #2d3748);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.ladder-scroll-wrapper::-webkit-scrollbar {
  height: 8px;
}

.ladder-scroll-wrapper::-webkit-scrollbar-track {
  background: var(--bg-muted, #171d2b);
  border-radius: 4px;
}

.ladder-scroll-wrapper::-webkit-scrollbar-thumb {
  background: var(--border-color, #3b475d);
  border-radius: 4px;
}

.ladder-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: var(--accent-color, #3b82f6);
}

/* 矩阵网格 */
.ladder-grid {
  display: flex;
  flex-direction: column;
}

.ladder-row {
  display: flex;
  flex-direction: row;
  min-height: 48px;
}

/* 固定左侧列 */
.ladder-header-col {
  width: 105px;
  min-width: 105px;
  max-width: 105px;
  position: sticky;
  left: 0;
  z-index: 10;
  background: var(--bg-muted, #1a2333);
  border-color: var(--border-color, #2d3748) !important;
}

.corner-col {
  background: var(--bg-card, #1e2638) !important;
  z-index: 20;
}

.corner-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-main, #f1f5f9);
}

.corner-sub {
  font-size: 11px;
  color: var(--text-muted, #94a3b8);
  margin-top: 2px;
}

.tier-col {
  padding: 8px 4px;
}

.tier-name-badge {
  font-size: 13px;
}

.latest-tier-count {
  font-size: 11px;
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  padding: 1px 5px;
  border-radius: 10px;
  font-weight: 600;
}

.tier-total-hint {
  color: var(--text-muted, #94a3b8);
  margin-top: 4px;
  font-size: 11px;
}

/* 日期列 */
.ladder-day-col {
  flex: 1 0 160px;
  min-width: 160px;
  border-color: var(--border-color, #2d3748) !important;
}

.ladder-header-row {
  position: sticky;
  top: 0;
  z-index: 15;
  background: var(--bg-card, #1e2638);
}

.date-short {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-main, #f1f5f9);
}

.weekday-badge {
  font-size: 11px;
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(59, 130, 246, 0.15);
  color: var(--accent-color, #3b82f6);
  font-weight: 600;
}

.latest-tag {
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 3px;
  background: #ef4444;
  color: #fff;
  font-weight: 700;
  animation: pulse-badge 2s infinite ease-in-out;
}

@keyframes pulse-badge {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.85; transform: scale(1.05); }
}

.dot-sep {
  margin: 0 4px;
}

.max-pct-text {
  color: #ef4444;
  font-weight: 600;
}

.latest-col-header {
  background: rgba(59, 130, 246, 0.08);
  border-bottom: 2px solid var(--accent-color, #3b82f6) !important;
}

.latest-col-cell {
  background: rgba(59, 130, 246, 0.02);
}

.empty-cell {
  background: rgba(0, 0, 0, 0.03);
}

body.theme-dark .empty-cell {
  background: rgba(0, 0, 0, 0.15);
}

/* 股票卡片样式 */
.stock-item-card {
  padding: 6px 8px;
  border-radius: 6px;
  background: var(--bg-card, #1e2638);
  border: 1px solid var(--border-color, #334155);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.stock-item-card:hover {
  transform: translateY(-1px);
  border-color: var(--accent-color, #3b82f6);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.18);
}

/* 同股多日高亮脉冲效果 */
.stock-item-card.same-stock-highlight {
  border-color: #f59e0b !important;
  background: rgba(245, 158, 11, 0.18) !important;
  box-shadow: 0 0 10px rgba(245, 158, 11, 0.5) !important;
  transform: scale(1.03);
  z-index: 5;
  position: relative;
}

.stock-name {
  font-size: 13px;
  color: var(--text-main, #f1f5f9);
  letter-spacing: 0.3px;
}

.stock-pct-rate {
  font-size: 12px;
  font-family: monospace;
  font-weight: 700;
}

.pct-up {
  color: #ef4444;
}

.pct-down {
  color: #22c55e;
}

.circle-badge {
  font-size: 11px;
  line-height: 1;
}

.circle-c { color: #38bdf8; }
.circle-k { color: #f43f5e; }
.circle-b { color: #a855f7; }

.stock-real-row {
  border-top: 1px dashed var(--border-color, #334155);
  padding-top: 2px;
  font-size: 11px;
}

/* 题材板块药丸 */
.stock-card-plates {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.plate-badge {
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(148, 163, 184, 0.12);
  color: var(--text-muted, #94a3b8);
  max-width: 80px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.plate-more {
  font-size: 10px;
  color: var(--text-muted, #94a3b8);
  align-self: center;
}

.stock-card-price {
  font-size: 11px;
  font-family: monospace;
}
</style>
